import * as React from "react";
import { useSearchParams as useRouterSearchParams } from "react-router-dom";

export const useSearchParams = <
  TDefaults extends Record<
    string,
    string | number | boolean | Record<string, unknown>
  >,
>(
  defaults: TDefaults,
) => {
  const [searchParams, rawSetSearchParams] = useRouterSearchParams();

  const params = React.useMemo(() => {
    const next: Record<string, string | number | boolean | Record<string, unknown>> =
      {
        ...defaults,
      };
    for (const k of Object.keys(defaults)) {
      const raw = searchParams.get(k);
      const fallback = defaults[k];
      if (typeof fallback === "number") {
        if (raw == null || raw === "") {
          next[k] = fallback;
          continue;
        }
        const n = Number(raw);
        next[k] = Number.isFinite(n) ? n : fallback;
        continue;
      }
      if (typeof fallback === "boolean") {
        if (raw == null) {
          next[k] = fallback;
          continue;
        }
        if (raw === "") {
          next[k] = fallback;
          continue;
        }
        const v = raw.trim().toLowerCase();
        if (v === "true" || v === "1" || v === "yes" || v === "on") {
          next[k] = true;
        } else if (v === "false" || v === "0" || v === "no" || v === "off") {
          next[k] = false;
        } else {
          next[k] = fallback;
        }
        continue;
      }
      if (typeof fallback === "object" && fallback != null) {
        if (raw == null || raw === "") {
          next[k] = fallback;
          continue;
        }
        try {
          const parsed = JSON.parse(raw);
          if (typeof parsed === "object" && parsed != null) {
            next[k] = parsed as Record<string, unknown>;
          } else {
            next[k] = fallback;
          }
        } catch {
          next[k] = fallback;
        }
        continue;
      }
      next[k] = raw == null ? fallback : raw;
    }
    return next as TDefaults;
  }, [defaults, searchParams]);

  const setParams = React.useCallback(
    (
      updates: Record<
        string,
        string | number | boolean | Record<string, unknown> | null | undefined
      >,
    ) => {
      const next = new URLSearchParams(searchParams);
      for (const [k, v] of Object.entries(updates)) {
        if (v == null) next.delete(k);
        else if (v === "") next.set(k, "");
        else if (typeof v === "object") next.set(k, JSON.stringify(v));
        else next.set(k, String(v));
      }
      rawSetSearchParams(next, { replace: true });
    },
    [rawSetSearchParams, searchParams],
  );

  return { searchParams: params, setSearchParams: setParams };
};
